import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import type { CSSProperties } from 'react';
import type { SubscriptionTier } from '../../types/subscription';
import {
  NAV_ITEMS,
  shouldShowProBadge,
  PRO_BADGE_STYLE,
  TIER_BADGE_CONFIG,
  type NavItemDef,
} from './nav-config';

function ProBadge() {
  return <span style={PRO_BADGE_STYLE}>PRO</span>;
}

function TierBadge({ tier }: { tier: SubscriptionTier }) {
  const config = TIER_BADGE_CONFIG[tier];

  const style: CSSProperties = {
    borderRadius: 9999,
    padding: '4px 12px',
    fontSize: 12,
    fontWeight: 600,
    backgroundColor: config.bg,
    color: config.color,
    lineHeight: '16px',
  };

  return <span style={style}>{config.label}</span>;
}

/* ---- NavItem with hover tracking ---- */

interface NavItemProps {
  item: NavItemDef;
  showBadge: boolean;
}

function NavItem({ item, showBadge }: NavItemProps) {
  const [hovered, setHovered] = useState(false);
  const Icon = item.icon;

  const baseStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    paddingTop: 10,
    paddingBottom: 10,
    paddingRight: 12,
    paddingLeft: 12,
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 500,
    textDecoration: 'none',
    transition: 'background-color 0.15s, color 0.15s',
    cursor: 'pointer',
    borderTop: 'none',
    borderRight: 'none',
    borderBottom: 'none',
    borderLeft: 'none',
    width: '100%',
    boxSizing: 'border-box' as const,
  };

  return (
    <NavLink
      to={item.path}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={({ isActive }) => {
        if (isActive) {
          return {
            ...baseStyle,
            backgroundColor: 'rgba(99,102,241,0.1)',
            color: '#6366f1',
            borderLeft: '3px solid #6366f1',
            paddingLeft: 9, // compensate for 3px border
          };
        }
        return {
          ...baseStyle,
          backgroundColor: hovered ? 'rgba(255,255,255,0.05)' : 'transparent',
          color: hovered ? '#ffffff' : 'rgba(255,255,255,0.7)',
        };
      }}
    >
      <Icon style={{ width: 20, height: 20, flexShrink: 0 }} />
      <span>{item.label}</span>
      {showBadge && <ProBadge />}
    </NavLink>
  );
}

/* ---- Sidebar ---- */

export interface SidebarProps {
  currentTier?: SubscriptionTier;
}

export function Sidebar({ currentTier = 'free' }: SidebarProps) {
  const asideStyle: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: 256,
    backgroundColor: '#111118',
    borderRight: '1px solid rgba(255,255,255,0.06)',
  };

  const logoAreaStyle: CSSProperties = {
    display: 'flex',
    height: 64,
    alignItems: 'center',
    gap: 8,
    padding: '0 24px',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
  };

  const logoIconStyle: CSSProperties = {
    display: 'flex',
    height: 32,
    width: 32,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 8,
    backgroundColor: '#6366f1',
  };

  const logoLetterStyle: CSSProperties = {
    fontSize: 14,
    fontWeight: 700,
    color: '#ffffff',
  };

  const brandTextStyle: CSSProperties = {
    fontSize: 18,
    fontWeight: 700,
    color: '#ffffff',
  };

  const brandAccentStyle: CSSProperties = {
    color: '#6366f1',
  };

  const navStyle: CSSProperties = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    padding: '16px 12px',
    overflowY: 'auto',
  };

  const tierAreaStyle: CSSProperties = {
    borderTop: '1px solid rgba(255,255,255,0.06)',
    padding: 16,
  };

  const tierInnerStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  };

  const tierLabelStyle: CSSProperties = {
    fontSize: 12,
    color: 'rgba(255,255,255,0.5)',
  };

  return (
    <aside style={asideStyle}>
      {/* Logo */}
      <div style={logoAreaStyle}>
        <div style={logoIconStyle}>
          <span style={logoLetterStyle}>C</span>
        </div>
        <span style={brandTextStyle}>
          CiteOps<span style={brandAccentStyle}>.ai</span>
        </span>
      </div>

      {/* Navigation */}
      <nav style={navStyle}>
        {NAV_ITEMS.map((item) => {
          const showBadge = shouldShowProBadge(item.requiresTier, currentTier);
          return (
            <NavItem key={item.path} item={item} showBadge={showBadge} />
          );
        })}
      </nav>

      {/* Tier badge at bottom */}
      <div style={tierAreaStyle}>
        <div style={tierInnerStyle}>
          <span style={tierLabelStyle}>Current Plan</span>
          <TierBadge tier={currentTier} />
        </div>
      </div>
    </aside>
  );
}
